import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";

const CONSENT_KEY = "tf_cookie_consent"; // "accepted" | "rejected"
const CONSENT_EVENT = "tf-cookie-consent-change";

/**
 * Read the current cookie-consent state. Returns "accepted", "rejected", or
 * null when the user hasn't decided yet.
 */
export function getCookieConsent() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(CONSENT_KEY);
}

/**
 * Programmatically open the banner again (e.g. from the Cookie Policy page).
 * Clears the stored choice and broadcasts a change event so the banner shows.
 */
export function openCookieConsent() {
  localStorage.removeItem(CONSENT_KEY);
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

/**
 * Cookie consent banner.
 * - Hidden once the user picks Accept or Reject.
 * - Stays out of the way of the PWA install prompt + mobile nav (uses
 *   safe-area + 84px bottom inset on small screens).
 */
export default function CookieConsent() {
  const { t, lang } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const check = () => setVisible(!getCookieConsent());
    check();
    const onChange = () => check();
    window.addEventListener(CONSENT_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const decide = (choice) => {
    localStorage.setItem(CONSENT_KEY, choice);
    window.dispatchEvent(new Event(CONSENT_EVENT));
    setVisible(false);
  };

  if (!visible) return null;

  const labels = lang === "da"
    ? {
        title: "Vi bruger cookies",
        body: <>Vi bruger kun nødvendige cookies for at appen virker — fx for at holde dig logget ind. Du kan læse mere i vores <Link to="/cookies" className="underline" style={{ color: "var(--tf-blue)" }}>cookiepolitik</Link>.</>,
        accept: "Accepter",
        reject: "Kun nødvendige",
        close: "Luk",
      }
    : {
        title: "We use cookies",
        body: <>We only use essential cookies to make the app work — like keeping you signed in. Read more in our <Link to="/cookies" className="underline" style={{ color: "var(--tf-blue)" }}>Cookie Policy</Link>.</>,
        accept: "Accept",
        reject: "Essential only",
        close: "Close",
      };

  // Suppress the toast-style banner on the Cookie Policy page itself —
  // we don't want it covering the policy the user is reading.
  if (typeof window !== "undefined" && window.location.pathname === "/cookies") {
    return null;
  }

  return (
    <div
      data-testid="cookie-consent"
      className="fixed inset-x-3 z-50 sm:left-6 sm:right-auto sm:max-w-md"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)" }}
      role="dialog"
      aria-live="polite"
      aria-label={labels.title}
    >
      <div
        className="border shadow-2xl backdrop-blur-md p-4"
        style={{
          background: "color-mix(in oklab, var(--tf-surface) 94%, transparent)",
          borderColor: "var(--tf-border)",
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="h-10 w-10 flex items-center justify-center shrink-0"
            style={{ background: "var(--tf-surface-2)", border: "1px solid var(--tf-border)" }}
            aria-hidden="true"
          >
            <Cookie weight="duotone" size={20} color="#FFCC00" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-base leading-tight tracking-tight" style={{ color: "var(--tf-text)" }}>
              {labels.title}
            </div>
            <div className="text-xs mt-1 leading-snug" style={{ color: "var(--tf-text-2)" }}>
              {labels.body}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="cookie-accept"
                onClick={() => decide("accepted")}
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition active:scale-95 text-white"
                style={{ background: "var(--tf-blue)" }}
              >
                {labels.accept}
              </button>
              <button
                type="button"
                data-testid="cookie-reject"
                onClick={() => decide("rejected")}
                className="px-3 py-1.5 text-xs font-semibold uppercase tracking-widest border transition active:scale-95"
                style={{ borderColor: "var(--tf-border)", color: "var(--tf-text-2)" }}
              >
                {labels.reject}
              </button>
            </div>
          </div>
          <button
            type="button"
            data-testid="cookie-close"
            onClick={() => decide("rejected")}
            aria-label={labels.close}
            className="shrink-0 p-1 transition hover:opacity-80"
            style={{ color: "var(--tf-text-2)" }}
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
